import React from 'react';

interface TemplateCard {
  title: string;
  description: string;
  channels: string[];
}

const templates: TemplateCard[] = [
  {
    title: "Abandoned Cart Recovery",
    description: "Recover lost revenue with WhatsApp nudge and Email fallback.",
    channels: ["WhatsApp", "Email"]
  },
  {
    title: "Win-back Churn Risk",
    description: "Target users with decreased activity via SMS and RCS.",
    channels: ["SMS", "RCS"]
  },
  {
    title: "Product Launch",
    description: "Announce with RCS rich cards and follow-up Email.",
    channels: ["RCS", "Email"]
  },
  {
    title: "Order Updates",
    description: "Transactional journeys via SMS with Voice fallover.",
    channels: ["SMS", "Voice"]
  },
  {
    title: "Welcome Series",
    description: "New users onboarding across Email + WhatsApp.",
    channels: ["Email", "WhatsApp"]
  },
  {
    title: "COD Confirmation",
    description: "Reduce RTO with WhatsApp confirmation and IVR.",
    channels: ["WhatsApp", "Voice"]
  }
];

const ChannelBadge: React.FC<{ channel: string }> = ({ channel }) => {
  const getChannelStyle = (channel: string) => {
    const baseClasses = "px-3 py-1 rounded-full text-sm font-medium";
    
    switch (channel.toLowerCase()) {
      case 'whatsapp':
        return `${baseClasses} bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400`;
      case 'email':
        return `${baseClasses} bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400`;
      case 'sms':
        return `${baseClasses} bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400`;
      case 'rcs':
        return `${baseClasses} bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400`;
      case 'voice':
        return `${baseClasses} bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400`;
      default:
        return `${baseClasses} bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300`;
    }
  };

  return (
    <span className={getChannelStyle(channel)}>
      {channel}
    </span>
  );
};

const TemplateCard: React.FC<{ template: TemplateCard }> = ({ template }) => {
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-md transition-shadow duration-200">
      <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
        {template.title}
      </h3>

      <p className="text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
        {template.description}
      </p>
      
      <div className="flex flex-wrap gap-2 mb-6">
        {template.channels.map((channel, index) => (
          <ChannelBadge key={index} channel={channel} />
        ))}
      </div>
      
      <button className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-2 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
        Use template
      </button>
    </div>
  );
};

const UseCases: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Marketing Templates
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Choose from pre-built templates to create effective multi-channel campaigns
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template, index) => (
            <TemplateCard key={index} template={template} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default UseCases;